/**
 * Copyright 2023 LiveKit, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { LiveKitRoom, RoomAudioRenderer, useRoomContext, useTracks } from '@livekit/components-react';
import type { TrackReferenceOrPlaceholder } from '@livekit/components-core';
import { ParticipantInfo_Kind as ParticipantKind } from '@livekit/protocol';
import EgressHelper from '@livekit/egress-sdk';
import { ConnectionState, RoomEvent, Track } from 'livekit-client';
import { ReactElement, useEffect, useState } from 'react';
import { CustomLayout } from './components/layout/CustomLayout';

interface RoomPageProps {
  url: string;
  token: string;
}

export interface UserMetadata {
  user_id: string;
  username: string;
  profile_picture: string;
  can_push_message: boolean;
  is_host: boolean;
}

function filterTracks(tracks: TrackReferenceOrPlaceholder[]): TrackReferenceOrPlaceholder[] {
  const filteredTracks = tracks.filter((tr) => tr.participant?.permissions?.canPublish === true || tr.participant.kind === ParticipantKind.INGRESS);

  const hostIndex = filteredTracks.findIndex((t) => {
    try {
      const metadata: UserMetadata | null = t.participant.metadata ? JSON.parse(t.participant.metadata) : null;
      return metadata && metadata.is_host;
    } catch (error) {
      return false;
    }
  });

  if (hostIndex !== -1) {
    const updatedTracks = [...filteredTracks];
    const hostUser = updatedTracks.splice(hostIndex, 1)[0];
    updatedTracks.unshift(hostUser);
    return updatedTracks;
  }

  return filteredTracks;
}

export default function RoomPage({ url, token }: RoomPageProps) {
  const [error, setError] = useState<Error>();
  if (!url || !token) {
    return <div className="error">missing required params url and token</div>;
  }

  return (
    <LiveKitRoom serverUrl={url} token={token} onError={setError}>
      {error ? <div className="error">{error.message}</div> : <CompositeTemplate />}
    </LiveKitRoom>
  );
}

function CompositeTemplate() {
  const room = useRoomContext();

  useEffect(() => {
    if (room) {
      EgressHelper.setRoom(room);

      // start recording when there's already a track published
      let hasTrack = false;
      for (const p of Array.from(room.remoteParticipants.values())) {
        if (p.trackPublications.size > 0) {
          hasTrack = true;
          break;
        }
      }

      if (hasTrack) {
        EgressHelper.startRecording();
      } else {
        room.once(RoomEvent.TrackSubscribed, () => EgressHelper.startRecording());
      }
    }
  }, [room]);

  const allTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: true },
  );
  const filteredTracks = filterTracks(allTracks);

  let main: ReactElement = <></>;
  if (room.state !== ConnectionState.Disconnected) {
    main = <CustomLayout tracks={filteredTracks} />;
  }

  return (
    <div className="roomContainer">
      {main}
      <RoomAudioRenderer />
    </div>
  );
}
